import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft, Ship, Bike, MapPin, Car, Clock, Phone, FileText, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQPage() {
  const navigate = useNavigate();
  const [customMedia, setCustomMedia] = useState([]);
  const [dbFaqs, setDbFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMedia();
    fetchFaqs();
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setCustomMedia(res.data || []);
    } catch (e) {
      console.error("Error fetching media:", e);
    }
  };

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/faqs`);
      setDbFaqs(res.data || []);
    } catch (e) {
      console.error("Error fetching FAQs:", e);
    } finally {
      setLoading(false);
    }
  };

  // Group FAQs by category for display
  const groupedFaqs = dbFaqs.reduce((acc, faq) => {
    const category = faq.category || "General";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {});

  // Category icons mapping
  const categoryIcons = {
    "Getting to the Island": Ship,
    "Getting Around the Island": Bike,
    "The Property": MapPin,
    "Booking & Policies": Clock,
    "General": FileText
  };

  // Default FAQs (fallback if database is empty)
  const defaultFaqs = [
    {
      category: "Getting to the Island",
      icon: Ship,
      questions: [
        {
          q: "How do I get to Long Island?",
          a: "Long Island is accessible via Casco Bay Ferry Lines from Portland, Maine. The ferry terminal is located at 56 Commercial Street in Portland's Old Port. The scenic ride takes about 45-50 minutes and offers beautiful views of Casco Bay and its islands."
        },
        {
          q: "Where can I find the ferry schedule?",
          a: "Visit cascobaylines.com for current schedules and fares. We recommend booking your ferry tickets in advance during peak summer months. The ferry runs multiple times daily year-round."
        },
        {
          q: "Is there parking at the ferry terminal?",
          a: "Yes, there is paid parking available at the Casco Bay Ferry Terminal in Portland. There are also several parking garages nearby in the Old Port district."
        }
      ]
    },
    {
      category: "Getting Around the Island",
      icon: Bike,
      questions: [
        {
          q: "What transportation options are available on the island?",
          a: "People use all kinds of transportation on the island! The most popular options are golf carts, bikes, and walking. The island is very walkable and most destinations are within easy reach."
        },
        {
          q: "Can I rent a golf cart?",
          a: "Yes! Golf carts are a fun way to explore the island. Per Maine law, you must have a valid driver's license to operate one. We highly recommend reserving rentals through Long Island Golf Carts in advance, especially during busy summer weekends."
        },
        {
          q: "What about bikes?",
          a: "You can bring your own bike on the ferry, or feel free to borrow a couple of our \"beater bikes\" from the shed at the inn—they're perfect for cruising around the island!"
        },
        {
          q: "How far is the inn from the ferry pier?",
          a: "The inn is about a 15-minute walk from the pier. We're located on the west end of the island, just a short stroll from the main beaches (South Beach, Fowler Beach, and Wreck Cove), the Bakehouse, and the Boathouse."
        }
      ]
    },
    {
      category: "The Property",
      icon: MapPin,
      questions: [
        {
          q: "How many guests can the inn accommodate?",
          a: "Chestnut Hill Inn comfortably accommodates up to 16 guests across 7 beautifully appointed bedrooms. Four bedrooms have private ensuites, and there are 6 bathrooms total."
        },
        {
          q: "Can I see the bedroom layout?",
          a: "Yes! View or download our upstairs bedroom layout below.",
          hasMedia: true,
          mediaType: "pdf",
          mediaUrl: "https://customer-assets.emergentagent.com/job_bookmate-32/artifacts/p8k51ykc_209%20Upstairs.pdf",
          mediaLabel: "View Upstairs Bedroom Layout (PDF)"
        },
        {
          q: "Is the inn suitable for weddings and events?",
          a: "Absolutely! The inn is perfect for intimate weddings, family reunions, corporate retreats, and girlfriend getaways. The spacious grounds and gathering areas provide wonderful spaces for celebrations."
        },
        {
          q: "What beaches are nearby?",
          a: "We're located just a short distance from three of Maine's most beautiful quiet beaches: South Beach, Fowler Beach, and Wreck Cove. Each offers a unique coastal experience with stunning views of Casco Bay."
        },
        {
          q: "What amenities are included?",
          a: "The inn includes a full commercial kitchen, WiFi throughout, all linens and towels, outdoor gathering spaces, and complimentary use of our \"beater bikes.\" The historic property has been beautifully renovated while maintaining its 1900s charm."
        }
      ]
    },
    {
      category: "Booking & Policies",
      icon: Clock,
      questions: [
        {
          q: "What is the nightly rate?",
          a: "The inn starts at $800 per night plus a $350 cleaning fee. Maine rental tax of 9% is added to all bookings. The entire inn is rented as a whole—it's not available by individual room."
        },
        {
          q: "Is there a minimum stay requirement?",
          a: "Please contact us for minimum stay requirements, which may vary by season. Summer weekends typically require a minimum stay."
        },
        {
          q: "What is the cancellation policy?",
          a: "Please contact us directly to discuss our cancellation policy and any specific circumstances."
        },
        {
          q: "What time is check-in and check-out?",
          a: "Standard check-in is at 4:00 PM and check-out is at 10:00 AM. Early check-in or late check-out may be available upon request—just let us know in advance!"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            data-testid="back-button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#78716C] hover:text-[#1C1917] transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          <a href="/" className="font-serif text-xl text-[#1C1917]">
            Chestnut Hill Inn
          </a>
          <Button
            onClick={() => navigate("/booking")}
            className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white text-xs uppercase tracking-widest"
          >
            Book Now
          </Button>
        </div>
      </header>

      <SEO 
        title="FAQ"
        description="Frequently asked questions about staying at Chestnut Hill Inn on Long Island, Maine. Learn about ferry access, check-in times, amenities, and booking policies."
        canonical="/faq"
      />

      {/* Hero */}
      <section className="bg-[#134E4A] py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-white/70 text-lg">
            Everything you need to know about staying at Chestnut Hill Inn
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#78716C]">Loading FAQs...</p>
            </div>
          ) : dbFaqs.length > 0 ? (
            /* Database FAQs grouped by category */
            Object.entries(groupedFaqs).map(([category, questions], idx) => {
              const IconComponent = categoryIcons[category] || FileText;
              return (
                <div key={idx} className="mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#e6f2f1] text-[#134E4A] flex items-center justify-center">
                      <IconComponent size={20} />
                    </div>
                    <h2 className="font-serif text-2xl text-[#1C1917]">
                      {category}
                    </h2>
                  </div>
                  
                  <Accordion type="single" collapsible className="space-y-3">
                    {questions.map((item, qIdx) => (
                      <AccordionItem
                        key={item.id || qIdx}
                        value={`${idx}-${qIdx}`}
                        className="bg-white border border-stone-200 px-6"
                      >
                        <AccordionTrigger className="text-left text-[#1C1917] hover:no-underline py-5">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-[#78716C] pb-5 leading-relaxed whitespace-pre-line">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              );
            })
          ) : (
            /* Fallback to default hardcoded FAQs if database is empty */
            defaultFaqs.map((category, idx) => (
              <div key={idx} className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#e6f2f1] text-[#134E4A] flex items-center justify-center">
                    <category.icon size={20} />
                  </div>
                  <h2 className="font-serif text-2xl text-[#1C1917]">
                    {category.category}
                  </h2>
                </div>
                
                <Accordion type="single" collapsible className="space-y-3">
                  {category.questions.map((item, qIdx) => (
                    <AccordionItem
                      key={qIdx}
                      value={`${idx}-${qIdx}`}
                      className="bg-white border border-stone-200 px-6"
                    >
                      <AccordionTrigger className="text-left text-[#1C1917] hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#78716C] pb-5 leading-relaxed">
                        {item.a}
                        {item.hasMedia && item.mediaType === "pdf" && (
                          <a
                            href={item.mediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-flex items-center gap-2 bg-[#134E4A] text-white px-4 py-2 text-sm hover:bg-[#0f3d3a] transition-colors"
                          >
                            <FileText size={16} />
                            {item.mediaLabel}
                            <ExternalLink size={14} />
                          </a>
                        )}
                        {item.hasMedia && item.mediaType === "image" && (
                          <img
                            src={item.mediaUrl}
                            alt={item.mediaLabel || ""}
                            className="mt-4 max-w-full h-auto border border-stone-200"
                          />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))
          )}

          {/* Contact CTA */}
          <div className="bg-[#F5F5F4] border border-stone-200 p-8 text-center mt-12">
            <h3 className="font-serif text-2xl text-[#1C1917] mb-3">
              Still have questions?
            </h3>
            <p className="text-[#78716C] mb-6">
              We&apos;re happy to help with any other questions about your stay.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/booking")}
                className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white px-8 py-6 text-xs uppercase tracking-widest"
              >
                Book Your Stay
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-stone-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-serif text-xl text-[#1C1917] mb-2">Chestnut Hill Inn</p>
          <p className="text-[#78716C] text-sm">
            209 Beach Ave, Long Island, Maine
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <a
              href="https://www.facebook.com/chestnut.hill.inn.and.retreat.center"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#78716C] hover:text-[#134E4A] transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/chestnuthillinn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#78716C] hover:text-[#134E4A] transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
          <p className="text-[#78716C] text-sm mt-4">
            © {new Date().getFullYear()} Chestnut Hill Inn. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
